import {
  DynamicUiData,
  DynamicUiEntityLayout,
  EntityFields,
  EntityMetadata,
  EntityWithFieldMetadata,
  EntityWithMetadata,
  FieldWithValue,
  FormValuesType,
  UiSection,
} from 'src/UI/dynamicUI/types';
import React from 'react';
import { UseFormReturnType } from '@mantine/form';
import { FeedInfo } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';
import { showError } from 'src/notifications/notifications';
import { FUND_HOLDING } from './FundHoldingFormAccordionItemList';

function buildUiDataForEntities(layout: DynamicUiEntityLayout, entities: string[]) {
  const uiData: DynamicUiData = {
    entities: {},
    ui: { ...layout },
  };

  entities.forEach(entityType => {
    const metadata: EntityMetadata = layout.entitiesMetadata?.[entityType] || {};
    const fields: EntityFields = Object.keys(metadata).reduce(
      (acc, key) => ({
        ...acc,
        [key]: undefined,
      }),
      {} as EntityFields,
    );

    uiData.entities[entityType] = {
      id: '',
      entityType: entityType,
      entityLabel: entityType,
      entity: {
        fields,
        metadata: metadata,
        entityType: entityType,
      },
    };
  });
  return uiData;
}

function isFieldWithValueArray(value: unknown): value is FieldWithValue[] {
  return (
    Array.isArray(value) &&
    value.every(v => typeof v === 'object' && v !== null && !Array.isArray(v))
  );
}

// Using this until I extract some of the dynamic UI provider stuff
export const fakeSection: UiSection = {
  type: 'UI_ENTITY',
  label: 'Label',
  children: [
    {
      type: 'FIELD',
      label: '',
      span: 0,
      entityType: '',
      entityField: 'string',
    },
  ],
};

type CreateProps = {
  layout: DynamicUiEntityLayout;
  onSave: (mappedValues: EntityFields | EntityFields[], metadata: EntityMetadata) => Promise<void>;
};

type UpdateProps = CreateProps & {
  onDeleteConfirm: () => void;
  onConnectAsset?: (id: string) => void;
  entityWithMetadata: EntityWithFieldMetadata;
};

export type ProductFormComponentProps = CreateProps | UpdateProps;
export type ProductFormComponent = React.ComponentType<ProductFormComponentProps>;

type ListDependency = {
  product: string;
  field: string;
};

const getUpdateInitialValues = (
  product: string,
  entityWithMetadata: EntityWithFieldMetadata,
  listDependencies: ListDependency[] = [],
): FormValuesType => {
  const dependenciesInitialValues = listDependencies.reduce(
    (acc, dependency) => {
      const value = entityWithMetadata.fields[dependency.field];
      acc[dependency.product] = isFieldWithValueArray(value) ? value : [];
      return acc;
    },
    {} as { [entityType: string]: FieldWithValue[] },
  );

  const fields = Object.keys(entityWithMetadata.fields).reduce((acc, field) => {
    const fieldValue = entityWithMetadata.fields[field];

    if (!Array.isArray(fieldValue) && entityWithMetadata.metadata[field]?.type === 'BOOLEAN') {
      return {
        ...acc,
        [field]: fieldValue === true || fieldValue === false ? fieldValue.toString() : undefined,
      };
    }

    return { ...acc, [field]: fieldValue };
  }, {} as EntityFields);

  return {
    [product]: fields,
    ...dependenciesInitialValues,
  };
};

const getCreateInitialValues = (
  product: string,
  uiData: DynamicUiData,
  listDependenciesProducts: string[] = [],
): FormValuesType => {
  const initialValues = (uiData.entities[product] as EntityWithMetadata).entity.fields;

  const emptyLists = listDependenciesProducts.reduce(
    (acc, product) => {
      acc[product] = [];
      return acc;
    },
    {} as { [entityType: string]: FieldWithValue[] },
  );

  return {
    [product]: initialValues,
    ...emptyLists,
  };
};

export const getFinancialFormSetupValues = (
  product: string,
  props: ProductFormComponentProps,
  listDependencies: ListDependency[] = [],
) => {
  const dependenciesProducts = listDependencies.map(d => d.product);
  const isCreateMode = !('entityWithMetadata' in props);
  const uiData = buildUiDataForEntities(props.layout, [product, ...dependenciesProducts]);

  const initialValues = isCreateMode
    ? getCreateInitialValues(product, uiData, dependenciesProducts)
    : getUpdateInitialValues(product, props.entityWithMetadata, listDependencies);

  const fields = isCreateMode ? {} : props.entityWithMetadata.fields;
  const isUpdatedAutomatically = fields['is_updated_automatically'] === true;
  const hasFeedData = fields['has_feed_data'] === true;
  const feedInfo: FeedInfo = { isUpdatedAutomatically, hasFeedData };
  return {
    isCreateMode,
    uiData,
    initialValues,
    feedInfo,
    onDeleteConfirm: isCreateMode ? () => {} : props.onDeleteConfirm,
    onConnectAsset: isCreateMode ? undefined : props.onConnectAsset,
  };
};

/**
 * Helper function to update feed info after asset creation if conditions are met.
 * This logic is common across all financial asset forms.
 */
export const updateFeedInfoAfterAssetCreation = (
  form: UseFormReturnType<FormValuesType>,
  productKey: string,
  isCreateMode: boolean,
  setFeedInfo: React.Dispatch<React.SetStateAction<FeedInfo>>,
) => {
  const formValues = form.getValues();
  const assetValues = formValues[productKey];
  const hasProviderRef =
    assetValues &&
    'provider_reference_number' in assetValues &&
    assetValues.provider_reference_number;

  if (isCreateMode && hasProviderRef) {
    setFeedInfo(prev => ({
      ...prev,
      hasFeedData: true,
    }));
  }
};

export const validateFundHoldingsAndDisplayError = (
  values: FormValuesType,
  form: UseFormReturnType<FormValuesType>
): boolean => {
  if (!values[FUND_HOLDING] || !Array.isArray(values[FUND_HOLDING])) {
    return true;
  }

  let hasValidationErrors = false;
  values[FUND_HOLDING].forEach((fundHolding: Record<string, unknown>, index: number) => {
    if (!fundHolding.fund_id) {
      form.setFieldError(
        `${FUND_HOLDING}.${index}.fund_id`,
        'Please select a fund for this holding'
      );
      hasValidationErrors = true;
    }
  });

  if (hasValidationErrors) {
    showError('Please select a fund for all fund holdings before saving.');
    return false;
  }

  return true;
};
